import { cn } from "./classNames-DkX30VJU.js";
import { Check, Circle } from "./icons-DDR9dFSr.js";
import { ScrollArea } from "./internal/components/ScrollArea.js";
import { TextInput } from "./TextInput-BAL-zOAN.js";
import { createContext, use } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Command, CommandInput, defaultFilter, useCommandState } from "cmdk";
// src/blocks/Command/CommandContext.tsx
/** Whether the enclosing `Command` is `compact`. */
const CommandContext = createContext(false);
const useCommandCompact = () => use(CommandContext);
// src/blocks/Command/CommandContent.tsx
const CommandContent = ({ className, ...rest }) => {
	const compact = useCommandCompact();
	return /* @__PURE__ */ jsx("div", {
		...rest,
		className: cn("tw:flex", "tw:flex-col", compact ? "tw:p-2" : "tw:p-4", "tw:gap-2", className)
	});
};
// src/blocks/Command/CommandEmpty.tsx
const CommandEmpty = ({ ...props }) => /* @__PURE__ */ jsx(Command.Empty, { ...props });
// src/blocks/Command/CommandGroup.tsx
const CommandGroup = ({ className, ...props }) => /* @__PURE__ */ jsx(Command.Group, {
	className: cn("tw:[&_[cmdk-group-heading]]:font-semibold", "tw:[&_[cmdk-group-heading]]:font-sans", "tw:[&_[cmdk-group-heading]]:px-4", "tw:[&_[cmdk-group-heading]]:py-0.5", "tw:[&_[cmdk-group-heading]]:uppercase", "tw:[&_[cmdk-group-heading]]:text-[11px]", "tw:[&_[cmdk-group-heading]]:text-muted-foreground", "tw:[&_[cmdk-group-heading]]:tracking-[1px]", "tw:[&_[cmdk-group-heading]]:border-b", "tw:[&_[cmdk-group-heading]]:border-border", "tw:[&_[cmdk-group-heading]]:my-2", className),
	...props
});
// src/blocks/Command/CommandInput.tsx
/** The search field; a bare full-width row unless the `Command` is `compact`. */
const CommandInput$1 = ({ ref, className, ...props }) => {
	const compact = useCommandCompact();
	return /* @__PURE__ */ jsx(CommandInput, {
		ref,
		asChild: true,
		children: /* @__PURE__ */ jsx(TextInput, {
			...props,
			className: cn(!compact && "tw:!bg-transparent tw:!border-transparent tw:!rounded-none tw:max-h-12 tw:h-12", className)
		})
	});
};
// src/blocks/Command/CommandItem/CheckIndicator.tsx
const CheckIndicator = ({ variant = "default" }) => {
	if (variant === "default") return null;
	return /* @__PURE__ */ jsx("span", {
		"data-variant": variant,
		className: cn("tw:group/check-indicator", "tw:pointer-events-none", "tw:size-4", "tw:flex", "tw:items-center", "tw:justify-center", "tw:border", "tw:border-solid", "tw:rounded", "tw:border-input", "tw:data-[variant=radio]:rounded-full", "tw:data-[variant=checkbox]:rounded", "tw:data-[variant=checkbox]:group-data-[checked=true]/command-item:border-primary", "tw:data-[variant=checkbox]:group-data-[checked=true]/command-item:bg-primary", "tw:shrink-0"),
		children: variant === "checkbox" ? /* @__PURE__ */ jsx(Check, { className: cn("tw:hidden", "tw:size-3", "tw:stroke-[4px]", "tw:group-data-[checked=true]/command-item:block", "tw:group-data-[variant=checkbox]/check-indicator:text-primary-foreground") }) : /* @__PURE__ */ jsx(Circle, { className: cn("tw:hidden", "tw:size-3", "tw:fill-current", "tw:group-data-[checked=true]/command-item:block", "tw:group-data-[variant=radio]/check-indicator:text-primary") })
	});
};
// src/blocks/Command/CommandItem/CommandItem.tsx
const CommandItem = ({ className, icon, children, checked = false, variant = "default", actions, ...props }) => /* @__PURE__ */ jsxs("div", {
	className: "tw:group tw:flex tw:items-center tw:justify-center",
	children: [/* @__PURE__ */ jsxs(Command.Item, {
		...props,
		"data-checked": checked,
		"data-variant": variant,
		className: cn("tw:group/command-item", "tw:peer/command-item", "tw:relative", "tw:flex", "tw:items-center", "tw:gap-2", "tw:flex-1", "tw:p-2", "tw:font-sans", "tw:text-sm", "tw:leading-6", "tw:cursor-pointer", "tw:select-none", "tw:outline-hidden", "tw:rounded-md", "tw:data-[selected=true]:bg-hover", "tw:aria-disabled:opacity-disabled", "tw:aria-disabled:cursor-not-allowed", "tw:aria-disabled:hover:bg-transparent", "tw:data-[checked=true]:aria-disabled:opacity-100", "tw:data-[checked=true]:aria-disabled:cursor-default", className),
		children: [
			/* @__PURE__ */ jsx(CheckIndicator, { variant }),
			icon && /* @__PURE__ */ jsx("span", {
				className: "tw:inline-flex tw:size-4 tw:shrink-0 tw:items-center tw:justify-center tw:text-muted-foreground tw:[&_svg:not([class*='tw:size-'])]:size-4",
				children: icon
			}),
			children,
			checked && variant === "default" && /* @__PURE__ */ jsx(Check, {
				"aria-hidden": true,
				className: "tw:ml-auto tw:size-4 tw:shrink-0 tw:text-primary"
			})
		]
	}), actions && /* @__PURE__ */ jsx("span", {
		"data-slot": "command-item-actions",
		className: cn("tw:hidden", "tw:mx-2", "tw:group-hover:flex", "tw:peer-hover/command-item:flex", "tw:peer-data-[selected=true]/command-item:flex"),
		children: actions
	})]
});
// src/blocks/Command/CommandList.tsx
const CommandList = ({ ref, className, children, viewPortClassName, ...props }) => {
	const compact = useCommandCompact();
	return /* @__PURE__ */ jsx(ScrollArea, {
		ref,
		className: cn(!compact && "tw:px-4 tw:pb-4", className),
		viewPortClassName: cn("tw:[&_[cmdk-list-sizer]]:flex", "tw:[&_[cmdk-list-sizer]]:flex-col", viewPortClassName),
		children: /* @__PURE__ */ jsx(Command.List, {
			...props,
			children
		})
	});
};
// src/blocks/Command/CommandSeparator.tsx
const CommandSeparator = ({ className, ...props }) => /* @__PURE__ */ jsx(Command.Separator, {
	className: cn("tw:h-px", "tw:bg-border", "tw:my-2", className),
	...props
});
// src/blocks/Command/Command.tsx
const Command$1 = ({ className, compact = false, ...rest }) => /* @__PURE__ */ jsx(CommandContext, {
	value: compact,
	children: /* @__PURE__ */ jsx(Command, {
		className: cn("tw:group/command", "tailwindcss-preflight", className),
		...rest
	})
});
Command$1.Content = CommandContent;
Command$1.Separator = CommandSeparator;
Command$1.List = CommandList;
Command$1.Group = CommandGroup;
Command$1.Empty = CommandEmpty;
Command$1.Input = CommandInput$1;
Command$1.Item = CommandItem;
export { Command$1 as Command, CommandGroup, CommandInput$1 as CommandInput, CommandItem, CommandList, defaultFilter, useCommandState };

//# sourceMappingURL=Command-D6XYzzJf.js.map